import pick from 'lodash/pick';
import endOfDay from 'date-fns/endOfDay';
import endOfYear from 'date-fns/endOfYear';
import startOfYear from 'date-fns/startOfYear';
import endOfQuarter from 'date-fns/endOfQuarter';
import startOfQuarter from 'date-fns/startOfQuarter';
import endOfMonth from 'date-fns/endOfMonth';
import startOfMonth from 'date-fns/startOfMonth';
import subYears from 'date-fns/subYears';
import subQuarters from 'date-fns/subQuarters';
import subMonths from 'date-fns/subMonths';
// @ts-expect-error - TS2614 - Module '"date-fns/sub"' has no exported member 'Duration'. Did you mean to use 'import Duration from "date-fns/sub"' instead?
import sub, {Duration} from 'date-fns/sub';
import {
  getTodayInTimezone,
  addTimezoneStartOfDay,
  addTimezoneEndOfDay,
  formatIsoDate,
  getSubtractedDate,
} from '@spaced-out/ui-design-system/lib/utils';
import moment from 'moment-timezone';

import logger from 'src/utils/logger';


export type Range = {
  startDate: string;
  endDate: string;
};

const getStartDate = (timeAgo: Duration) => {
  try {
    const date = sub(endOfDay(new Date()), timeAgo);
    return date.toISOString();
  } catch (e: any) {
    console.log('Invalid date calculation', {timeAgo});
    // Return a safe fallback date
    return new Date().toISOString();
  }
};

type DateRangeQuery = {
  time_ago?: string;
  roi_time_frame?: TimeFrame;
  start_date?: string;
  end_date?: string;
};

type DateRangeQueryWithTimezone = DateRangeQuery & {
  timezone?: string;
};

// TODO (kyle): change this name to `resolveDateRangeQuery`
export function resolveDateRange<T extends DateRangeQuery>(
  range: T,
  pickProps?: string[] | null,
  defaultTimeAgo: Duration = {days: 90},
): T & {
  start_date: string;
  end_date: string;
} {
  let query = pickProps ? pick(range, pickProps) : range;

  if (range.time_ago) {
    const timeAgo = JSON.parse(range.time_ago);
    const start_date = getStartDate(timeAgo);

    query = {
      ...query,
      start_date,
      end_date: endOfDay(new Date()).toISOString(),
    };
  } else if (range.start_date && range.end_date) {
    query = {
      ...query,
      ...pick(range, 'start_date', 'end_date'),
    };
  } else {
    query = {
      ...query,
      start_date: getStartDate(defaultTimeAgo),
      end_date: endOfDay(new Date()).toISOString(),
    };
  }

  // @ts-expect-error - TS2322 - Type 'Partial<T>' is not assignable to type 'T & { start_date: string; end_date: string; }'.
  return query;
}

export function resolveDateRangeWithTimezone<
  T extends DateRangeQueryWithTimezone,
>(
  range: T,
  pickProps?: string[] | null,
  defaultTimeAgo: Duration = {days: 90},
): T & {
  start_date: string;
  end_date: string;
  timezone: string;
} {
  let query = pickProps ? pick(range, pickProps) : range;
  const timezone = String(range.timezone || '');

  const today = getTodayInTimezone(timezone);

  if (range.time_ago != null && typeof range.time_ago === 'string') {
    const timeAgo = JSON.parse(range.time_ago);
    let startDate;

    try {
      let days;
      if (typeof timeAgo === 'number' || typeof timeAgo === 'string') {
        days = Number(timeAgo);
      } else if (typeof timeAgo === 'object' && 'days' in timeAgo) {
        days = Number(timeAgo.days);
      } else {
        throw new Error('Invalid timeAgo format');
      }

      if (isNaN(days)) {
        throw new Error('Parsed value is not a valid number');
      }

      startDate = getSubtractedDate(today, days - 1, 'd');
    } catch (error: any) {
      startDate = getStartDate(timeAgo);
    }

    query = {
      ...query,
      start_date: addTimezoneStartOfDay(startDate, timezone),
      end_date: addTimezoneEndOfDay(today, timezone),
      timezone,
    };
  } else if (range.start_date != null && range.end_date != null) {
    const startDate =
      typeof range.start_date === 'string' ? range.start_date : today;
    const endDate = typeof range.end_date === 'string' ? range.end_date : today;

    query = {
      ...query,
      start_date: addTimezoneStartOfDay(startDate, timezone),
      end_date: addTimezoneEndOfDay(endDate, timezone),
      timezone,
    };
  } else {
    query = {
      ...query,
      start_date: addTimezoneStartOfDay(getStartDate(defaultTimeAgo), timezone),
      end_date: addTimezoneEndOfDay(today, timezone),
      timezone,
    };
  }

  // @ts-expect-error - TS2322 - Type 'Partial<T>' is not assignable to type 'T & { start_date: string; end_date: string; timezone: string; }'.
  return query;
}

export function resolveRoiDateRange<T extends DateRangeQuery>(
  range: T,
  pickProps?: string[] | null,
  defaultTimeAgo: Duration = {days: 90},
): T & {
  start_date: string;
  end_date: string;
} {
  let query = resolveDateRange(range, pickProps, defaultTimeAgo);
  if (range.roi_time_frame) {
    query = {
      ...query,
      ...resolveTimeFrame(range.roi_time_frame),
    };
  }
  return query;
}

export type TimeFrame =
  | 'this_year'
  | 'last_year'
  | 'this_quarter'
  | 'last_quarter'
  | 'this_month'
  | 'last_month';

export const defaultTimeFrame: 'this_year' = 'this_year';
export const timeFrames: Set<TimeFrame> = new Set([
  'this_year',
  'last_year',
  'this_quarter',
  'last_quarter',
  'this_month',
  'last_month',
]);

export function parseTimeFrame(
  timeFrame: string = defaultTimeFrame,
): TimeFrame {
  let result = timeFrame;
  // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'TimeFrame'.
  if (!timeFrames.has(timeFrame)) {
    logger.warning(`Invalid timeframe ${timeFrame}`);
    result = defaultTimeFrame;
  }
  // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'TimeFrame'.
  return result;
}

export function resolveTimeFrame(
  timeFrame: TimeFrame = defaultTimeFrame,
  timezone: string = '',
): {
  start_date: string;
  end_date: string;
} {
  const today = getTodayInTimezone(timezone);
  let startDate;
  let endDate;

  switch (timeFrame) {
    case 'last_year':
      const previousYear = moment.utc(today).subtract(1, 'y').year();
      startDate = `${previousYear}-01-01`;
      endDate = `${previousYear}-12-31`;
      break;
    case 'this_year':
      startDate = formatIsoDate(moment.utc(today).startOf('y'));
      endDate = today;
      break;
    case 'last_quarter':
      startDate = formatIsoDate(
        moment.utc(today).subtract(1, 'Q').startOf('Q'),
      );
      endDate = formatIsoDate(moment.utc(today).subtract(1, 'Q').endOf('Q'));
      break;
    case 'this_quarter':
      startDate = formatIsoDate(moment.utc(today).startOf('Q'));
      endDate = formatIsoDate(moment.utc(today).endOf('Q'));
      break;
    case 'last_month':
      startDate = formatIsoDate(
        moment.utc(today).subtract(1, 'M').startOf('M'),
      );
      endDate = formatIsoDate(moment.utc(today).subtract(1, 'M').endOf('M'));
      break;
    case 'this_month':
      startDate = formatIsoDate(moment.utc(today).startOf('M'));
      endDate = formatIsoDate(moment.utc(today).endOf('M'));
      break;
    default:
      startDate = formatIsoDate(moment.utc(today).startOf('y'));
      endDate = today;
  }

  const start_date = addTimezoneStartOfDay(startDate, timezone);
  const end_date = addTimezoneEndOfDay(endDate, timezone);

  return {
    start_date,
    end_date,
  };
}
